import { useState, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Hook para detectar a largura de um container em tempo real.
 * Monitora mudanças de tamanho usando ResizeObserver.
 */
export function useContainerWidth() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(1200);
  const [height, setHeight] = useState<number>(800);
  const [isReady, setIsReady] = useState(false);

  // Monitora quando o container fica disponível
  useEffect(() => {
    if (containerRef.current && !isReady) {
      setIsReady(true);
    }
  });

  // useLayoutEffect executa de forma síncrona após o DOM ser atualizado
  useLayoutEffect(() => {
    const container = containerRef.current;
    
    if (!container) {
      return;
    }

    // Define a largura e altura inicial
    const updateDimensions = () => {
      if (container) {
        const newWidth = container.offsetWidth || container.clientWidth;
        const newHeight = container.offsetHeight || container.clientHeight;
        if (newWidth > 0) {
          setWidth(newWidth);
        }
        if (newHeight > 0) {
          setHeight(newHeight);
        }
      }
    };

    // Tenta atualizar imediatamente
    updateDimensions();
    
    // Tenta novamente após o próximo frame (garante que o layout foi calculado)
    requestAnimationFrame(() => {
      updateDimensions();
      // E mais uma vez para garantir
      requestAnimationFrame(updateDimensions);
    });

    // Cria um ResizeObserver para monitorar mudanças de tamanho
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === container) {
          const newWidth = entry.contentRect.width;
          const newHeight = entry.contentRect.height;
          if (newWidth > 0) {
            setWidth(newWidth);
          }
          if (newHeight > 0) {
            setHeight(newHeight);
          }
        }
      }
    });

    resizeObserver.observe(container);

    // Também escuta o evento resize da janela como fallback
    window.addEventListener('resize', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [isReady]); // Re-executa quando o container fica pronto

  return { containerRef, width, height };
}
